import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import { fm } from 'Components/FormatMessage';
import HistorySection from 'Components/HistorySection';
import HistoryTabs from 'Components/HistoryTabs';
import PlatformConditional from 'Components/PlatformConditional';
import { withCapabilities } from 'Containers/CapabilityHandler';
import { FormContextProvider, HistoryContextProvider, withHistoryContext } from 'Containers/FormContextHandler';
import FormEditingHandler from 'Containers/FormEditingHandler';
import * as React from 'react';
import { useAppSelector } from 'Store/index';
import { filterDocumentsByType } from 'Utility/documentHandling';
import PlatformCapabilities from '../../../config/capabilities';
import { assertCapabilities } from '../../../store/index';
import { parkinsonCriteriaTypes } from '../utils/definitions';
import { generateDxToPlatfMap } from '../utils/Diagnosis';
import { chooseDocumentHeader } from '../utils/functions';
import Form from './Form';
import DiagnosisHistory from './HistoryRowData/DiagnosisHistory';
import EtiologyHistory from './HistoryRowData/EpilepsyHistory/Etiology';
import SeizureTypeHistory from './HistoryRowData/EpilepsyHistory/SeizureType';
import SyndromeHistory from './HistoryRowData/EpilepsyHistory/Syndrome';
import HTT from './HistoryRowData/HuntingtonHistory/HTT';
import HuntingtonSymptoms from './HistoryRowData/HuntingtonHistory/HuntingtonSymptoms';
import UHDRS from './HistoryRowData/HuntingtonHistory/UHDRS';
import MGFA from './HistoryRowData/MgravisHistory/MGFA';
import MgravisSymptoms from './HistoryRowData/MgravisHistory/MgravisSymptoms';
import MSDiagnosticCriteriaHistory from './HistoryRowData/MSHistory/MsDiagnosticsCriteria/MS';
import NMOSDDiagnosticCriteriaHistory from './HistoryRowData/MSHistory/MsDiagnosticsCriteria/NMOSD';
import MsSymptoms from './HistoryRowData/MSHistory/MsSymptoms';
import NINMTHistory from './HistoryRowData/NINMTHistory';
import ParkinsonDiagnosticsCriteria from './HistoryRowData/ParkinsonHistory/ParkinsonDiagnosticsCriteria';
import PolyneuroSymptoms from './HistoryRowData/PolyneuroHistory/PolyneuroSymptoms';
import PolyneuroEtiologyHistory from './HistoryRowData/PolyneuroHistory/Etiology';
import PolyneuroUnderlyingConditionHistory from './HistoryRowData/PolyneuroHistory/UnderlyingCondition';
import ParkinsonSymptoms from './HistoryRowData/ParkinsonHistory/ParkinsonSymptoms';
import FirstVisitRespiratory from './HistoryRowData/SleepApneaHistory/FirstVisitRespiratory';
import FirstVisitSleepApnea from './HistoryRowData/SleepApneaHistory/FirstVisitSleepApnea';
import PerformanceRating from './HistoryRowData/SleepApneaHistory/PerformanceRating';
import SleepApneaSeverity from './HistoryRowData/SleepApneaHistory/SleepApneaSeverity';

import type { DocTypesByPageName } from '../../../config/platformSettings';
import type { ICapabilityContextProps } from 'Containers/CapabilityHandler';
import type { IHistoryContext } from 'Containers/FormContextHandler';

const MsHistoryContentComponent = (props: IHistoryContext & ICapabilityContextProps): React.JSX.Element => {
  const { capabilityGroups, documents = [], fm, startEdit, view } = props;
  const msDiagnosCriteriaDocs = filterDocumentsByType(documents, ['msDiagnosticCriteria']);
  const nmosdDiagnosCriteriaDocs = filterDocumentsByType(documents, ['nmosdDiagnosticCriteria']);
  const msSymptomDocs = filterDocumentsByType(documents, ['msSymptoms']);
  return (
    <React.Fragment>
      <MsSymptoms documents={msSymptomDocs} fm={fm} startEdit={startEdit} />
      <HistorySection headerText={fm('diagnosis.msDiagnosticCriteria.title')} hasHistoryRows={true}>
        <MSDiagnosticCriteriaHistory fm={fm} startEdit={startEdit} documents={msDiagnosCriteriaDocs} view={view} />
        {assertCapabilities([PlatformCapabilities.NMOSD_CAPABILITES], capabilityGroups ?? {}) ? (
          <NMOSDDiagnosticCriteriaHistory
            documents={nmosdDiagnosCriteriaDocs}
            fm={fm}
            startEdit={startEdit}
            view={view}
          />
        ) : undefined}
      </HistorySection>
    </React.Fragment>
  );
};

const MsHistoryContent = withCapabilities(withHistoryContext(MsHistoryContentComponent));

const ParkinsonHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const { documents = [] } = props;
  const parkSymptomsDocs = filterDocumentsByType(documents, ['parkinsonSymptoms']);
  const parkDiagCriteriaDocs = filterDocumentsByType(documents, parkinsonCriteriaTypes);

  return (
    <React.Fragment>
      <ParkinsonSymptoms {...props} documents={parkSymptomsDocs} />
      <ParkinsonDiagnosticsCriteria {...props} documents={parkDiagCriteriaDocs} />
    </React.Fragment>
  );
};

const ParkinsonHistoryContent = withHistoryContext(ParkinsonHistoryContentComponent);

const HuntingtonHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const { documents = [] } = props;
  const huntingtonSymptomsDocs = filterDocumentsByType(documents, ['huntingtonSymptoms']);
  const httDocs = filterDocumentsByType(documents, ['htt']);
  const uhdrsDocs = filterDocumentsByType(documents, ['uhdrsConfidenceIndex']);

  return (
    <React.Fragment>
      <HuntingtonSymptoms {...props} documents={huntingtonSymptomsDocs} />
      <HTT {...props} documents={httDocs} />
      <UHDRS {...props} documents={uhdrsDocs} />
    </React.Fragment>
  );
};

const HuntingtonHistoryContent = withHistoryContext(HuntingtonHistoryContentComponent);

const EpilepsyHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const { documents = [] } = props;
  const etiologyDocs = filterDocumentsByType(documents, ['etiology']);
  const seizureTypeDocs = filterDocumentsByType(documents, ['seizureType']);
  const syndromeDocs = filterDocumentsByType(documents, ['syndrome']);

  return (
    <React.Fragment>
      <SeizureTypeHistory {...props} documents={seizureTypeDocs} />
      <SyndromeHistory {...props} documents={syndromeDocs} />
      <EtiologyHistory {...props} documents={etiologyDocs} />
    </React.Fragment>
  );
};

const EpilepsyHistoryContent = withHistoryContext(EpilepsyHistoryContentComponent);

const MgravisHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const { documents = [] } = props;
  const mgSymptomsDocs = filterDocumentsByType(documents, ['mgravisSymptoms']);
  const mgfaDocs = filterDocumentsByType(documents, ['mgfa']);

  return (
    <React.Fragment>
      <MgravisSymptoms {...props} documents={mgSymptomsDocs} />
      <MGFA {...props} documents={mgfaDocs} />
    </React.Fragment>
  );
};

const MgravisHistoryContent = withHistoryContext(MgravisHistoryContentComponent);

const DEFAULT_DOCUMENTS: IDocumentWithTypeV2<DocTypesByPageName['diagnosis']>[] = [];
const SleepApneaHistoryContent = ({
  documents = DEFAULT_DOCUMENTS,
  startEdit,
  fm,
}: IHistoryContext<IDocumentWithTypeV2<DocTypesByPageName['diagnosis']>>): React.JSX.Element => {
  const diagDocs = filterDocumentsByType(documents, ['diagnosis']);
  const sleepApneaDG = diagDocs.find(d => d.diagnosis === 'G47.3');
  const sleepApneaSusDG = diagDocs.find(d => d.diagnosis === 'sleepApneaSuspicion');
  const respiratoryDG = diagDocs.find(d => d.diagnosis === 'J96.1' || d.diagnosis === 'J96.9');
  const respiratorySusDG = diagDocs.find(d => d.diagnosis === 'respiratoryFailureSuspicion');

  const sleepApneaFirstVisitDocs = filterDocumentsByType(documents, ['sleepApneaFirstVisit']);
  const sleepApneaSeverityDocs = filterDocumentsByType(documents, ['sleepApneaSeverity']);
  const respFirstVisitDocs = filterDocumentsByType(documents, ['respiratoryFirstVisit']);
  const performanceDocs = filterDocumentsByType(documents, ['performanceRating']);

  return (
    <React.Fragment>
      {(sleepApneaDG ?? sleepApneaSusDG) && (
        <React.Fragment>
          <FirstVisitSleepApnea documents={sleepApneaFirstVisitDocs} fm={fm} startEdit={startEdit} />
          {sleepApneaDG && <SleepApneaSeverity documents={sleepApneaSeverityDocs} fm={fm} startEdit={startEdit} />}
        </React.Fragment>
      )}
      {(respiratoryDG ?? respiratorySusDG) && (
        <FirstVisitRespiratory documents={respFirstVisitDocs} fm={fm} startEdit={startEdit} />
      )}
      <PerformanceRating documents={performanceDocs} fm={fm} startEdit={startEdit} />
    </React.Fragment>
  );
};

const SleepApneaHistoryContentWithContext = withHistoryContext(SleepApneaHistoryContent);

const NinmtHistoryContent = (): React.JSX.Element => <NINMTHistory />;

const PolyneuroHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const { documents = [] } = props;
  const polyneuroSymptomDocs = filterDocumentsByType(documents, ['polyneuroSymptoms']);
  const polyneuroEtiologyDocs = filterDocumentsByType(documents, ['polyneuroEtiology']);
  const polyneuroUCDocs = filterDocumentsByType(documents, ['polyneuroUnderlyingCondition']);

  return (
    <React.Fragment>
      <PolyneuroSymptoms {...props} documents={polyneuroSymptomDocs} />
      <PolyneuroEtiologyHistory {...props} documents={polyneuroEtiologyDocs} />
      <PolyneuroUnderlyingConditionHistory {...props} documents={polyneuroUCDocs} />
    </React.Fragment>
  );
};

const PolyneuroHistoryContent = withHistoryContext(PolyneuroHistoryContentComponent);

const platformsWithDiagnosis: Platform[] = [
  'sma',
  'dmd',
  'parkinson',
  'ms',
  'huntington',
  'epilepsy',
  'sleepApnea',
  'mgravis',
  'polyneuro',
];

const DiagnosisHistoryContent = ({ documents }: { documents: IDocumentV2[] }): React.JSX.Element => {
  const { selected, available = [], preferred = [] } = useAppSelector((s: IState) => s.session.platforms) ?? {};

  const showOthers = available.length > 1 || preferred.filter(p => p !== selected).length > 0;

  const dgDocs = filterDocumentsByType(documents, ['diagnosis']);
  const platformMap = generateDxToPlatfMap();

  const platformDocs = dgDocs.filter(d => d.diagnosis && platformMap[d.diagnosis] === selected);
  const otherDocs = dgDocs.filter(d => d.diagnosis && platformMap[d.diagnosis] !== selected);

  return (
    <React.Fragment>
      <PlatformConditional platform={platformsWithDiagnosis}>
        <HistoryTabs>
          {[
            {
              title: `header.platforms.${selected}`,
              content: (
                <>
                  {platformDocs.length === 0 ? (
                    <div style={{ marginBottom: '4rem' }}>{fm('diagnosis.noDiagnoses')}</div>
                  ) : (
                    <DiagnosisHistory documents={platformDocs} />
                  )}
                  <PlatformConditional platform="ms">
                    <MsHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="parkinson">
                    <ParkinsonHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="huntington">
                    <HuntingtonHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="epilepsy">
                    <EpilepsyHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="sleepApnea">
                    <SleepApneaHistoryContentWithContext />
                  </PlatformConditional>
                  <PlatformConditional platform="mgravis">
                    <MgravisHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="polyneuro">
                    <PolyneuroHistoryContent />
                  </PlatformConditional>
                </>
              ),
            },
            ...(showOthers
              ? [
                  {
                    title: 'diagnosis.otherDiagnoses',
                    content: <DiagnosisHistory documents={otherDocs} />,
                    count: otherDocs.length,
                    disabled: otherDocs.length === 0,
                  },
                ]
              : []),
          ]}
        </HistoryTabs>
      </PlatformConditional>

      <PlatformConditional platform="ninmt">
        <NinmtHistoryContent />
      </PlatformConditional>
    </React.Fragment>
  );
};

const DiagnosisDocumentHeader = ({
  name,
  headerId,
  editing,
  editButtons,
}: React.ComponentProps<typeof DocumentHeader>): React.JSX.Element => {
  const props = { name, headerId, editing, editButtons };
  return (
    <React.Fragment>
      <PlatformConditional platform="ninmt">
        <DocumentHeader
          {...props}
          headerId={editing ? props.headerId : 'diagnosis.basicTreatmentInformation'}
          infoText={
            !editing
              ? fm('diagnosis.ninmt.infoText')
              : props.headerId === 'diagnosis.ninmt.contraIndicationsToTreatment.title'
                ? fm('diagnosis.ninmt.contraIndicationsToTreatment.description')
                : props.headerId === 'diagnosis.basicTreatmentInformation'
                  ? fm('diagnosis.ninmt.infoText')
                  : props.headerId === 'diagnosis.ninmt.symptomsAndLocations.title'
                    ? fm('diagnosis.ninmt.symptomsAndLocationsInfoText')
                    : undefined
          }
          editButtons={undefined}
        />
      </PlatformConditional>
      <PlatformConditional platform={platformsWithDiagnosis}>
        <DocumentHeader {...props} infoText={!editing ? fm('diagnosis.infoText') : undefined} />
      </PlatformConditional>
    </React.Fragment>
  );
};

const Diagnosis = ({ documents }: IDiagnosisProps): React.JSX.Element => {
  const [diagnosisSelected, setDiagnosisSelected] = React.useState<boolean>(true);

  const platform = useAppSelector((s: IState) => s.session.platforms?.selected);

  return (
    <FormEditingHandler
      name="diagnosis"
      documents={documents}
      toolbarProps={{ saveButtonDisabled: !diagnosisSelected }}
    >
      {(editing, startEdit, formData, view, saveDraft): React.JSX.Element => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const docID = editing ?? view?.viewing;
        const editingNinmtTreatmentPeriod = !!documents.find(
          d => d._id === editing && d._type === 'ninmtTreatmentPeriod',
        );
        return (
          <DocumentWrapper>
            <DiagnosisDocumentHeader
              name="diagnosis"
              headerId={`diagnosis.${chooseDocumentHeader(documents, docID)}`}
              editing={docID}
              editButtons={
                <DocumentCreationButton
                  name="diagnosis"
                  text="general.new"
                  onClick={(e: React.MouseEvent): void => {
                    // In case a new diagnosis document is created, disable save button until diagnosis is set
                    setDiagnosisSelected(false);
                    startEdit({}, 'diagnosis')(e);
                  }}
                />
              }
            />
            {!docID || (platform === 'ninmt' && editingNinmtTreatmentPeriod) ? (
              <HistoryContextProvider context={{ documents, startEdit, view }}>
                <FormContextProvider context={{ editing, formData, saveDraft }}>
                  <DiagnosisHistoryContent documents={documents} />
                </FormContextProvider>
              </HistoryContextProvider>
            ) : null}
            {docID ? (
              <FormContextProvider context={{ documents, editing, formData, view }}>
                <Form setDiagnosisSelected={setDiagnosisSelected} />
              </FormContextProvider>
            ) : null}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IDiagnosisProps {
  documents: IDocumentWithTypeV2<DocTypesByPageName['diagnosis']>[];
}

export default Diagnosis;
